import { ref, reactive } from 'vue'
import {PI, cos, sin, toRads, arctan, sqrt, log, ln, exp, } from '../Common/Math.js'
import { describeFunc, Units, } from '../Common/CalcContext.js'

export let P_std = 14.69595;

// Absolute zero, in the various temperature units used throughout this file.
export let ABSOLUTE_ZERO_F = -459.67;
export let ABSOLUTE_ZERO_C = -273.15;
export let ABSOLUTE_ZERO_R = 0;

// Do not let the humidity ratio go below this value
export let MIN_HUMIDITY_RATIO = 1e-7;

// Note: these are input by the user. Defaults given here
//export let RH_winter_indoor = 30
//export let RH_summer_indoor = 50
// This one is actually used by House calcs
export let RH_winter_outdoor = 50
//export let RH_summer_outdoor = 50

export class ChartPoint {
  constructor(data) {
    this.name = data.name || "";
    this.dryBulbTemp = data.dryBulbTemp;
    this.wetBulbTemp = data.wetBulbTemp;
    this.relativeHumidity = data.relativeHumidity;
    this.humidityRatio = data.humidityRatio;
    this.dewpointTemp = data.dewpointTemp;
    this.specificVolume = data.specificVolume;
    this.enthalpy = data.enthalpy;
  }

  static createFromOutputs(outputs, opts) {
    let pointData = {
      name: opts?.name || "",
      dryBulbTemp: outputs.t_db_F,
      wetBulbTemp: outputs.t_wb_F,
      relativeHumidity: outputs.RH,
      humidityRatio: outputs.W,
      dewpointTemp: outputs.t_dp_F,
      specificVolume: outputs.v,
      enthalpy: outputs.h,
    }
    return new ChartPoint(pointData);
  }
}

export function calcLocalPressure(altFt) {
  // console.log(`Calculating P_loc: ${altFt}`);
  let res = 14.69595*((1.0 - altFt*6.8754e-06)**5.2559);
  // console.log("res: ", res);
  return res;
}

// Same as calcLocalPressure, but will show up in calc logs
export let calcP_loc = describeFunc('calcP_loc', {
  altitude_ft: 'ft',
}, 'psi', (ctx, altitude_ft) => {
  return ctx.eval('P_std*((1.0 - altitude_ft*6.8754e-06)^5.2559)', {
    P_std: P_std,
    altitude_ft: altitude_ft,
  }, 'P_loc');
});

export let adjustForAltitude = describeFunc('adjustForAltitude', {
  value: 'None',
  P_loc: 'psi',
}, 'None', (ctx, value, P_loc) => {
  return ctx.eval('value*(P_loc/P_std)', {
    value: value,
    P_loc: P_loc,
    P_std: P_std,
  }, 'adjusted');
});

export function toCelsius(tempF) {
  tempF = Math.max(tempF, ABSOLUTE_ZERO_F);
  return (tempF - 32) * 5/9.0
}

export function toFahr(tempC) {
  tempC = Math.max(tempC, ABSOLUTE_ZERO_C);
  return tempC*9/5.0 + 32;
}

export function toRankine(tempF) {
  tempF = Math.max(tempF, ABSOLUTE_ZERO_F);
  return tempF + 459.67;
}

// Tdb in C
// RH in [0-100]
// Result in C
export let calcTwb = describeFunc('calcTwb', {
    Tdb: 'C', RH: 'Percent'
  }, 'C', (ctx, Tdb, RH) => {
    return ctx.eval('Tdb * atan(0.151977 * sqrt(RH + 8.313659)) +' +
      'atan(Tdb + RH) - atan(RH - 1.676331) + 0.00391838 * (RH^1.5) * atan(0.023101 * RH) - 4.686035',
      {Tdb, RH}, 'Twb');
});

const C1 = -10214.165
const C2 = -4.8932428
const C3 = -0.005376579
const C4 = 0.00000019202377
const C5 = 3.5575832E-10
const C6 = -9.0344688E-14
const C7 = 4.1635019
const C8 = -10440.397
const C9 = -11.29465
const C10 = -0.027022355
const C11 = 0.00001289036
const C12 = -2.4780681E-09
const C13 = 6.5459673

// Saturation pressure of water
// Twb_R: Twb in R
export let calcPsat_W = describeFunc('calcPsat_W', {
  Twb_R: 'R'}, 'psi',
  (ctx, Twb_R) => {
    let X = ctx.eval('C8/Twb_R + C9 + C10*Twb_R + C11*(Twb_R^2) +' +
      'C12*(Twb_R^3) + C13*ln(Twb_R)', {
        Twb_R, C8, C9, C10, C11, C12, C13, ln,
      }, 'X');
    return ctx.eval('exp(X)', {X}, 'Psat_W');
});

// Saturation pressure of ice
// Twb_R: Twb in R
export let calcPsat_I = describeFunc('calcPsat_I', {
  Twb_R: 'R'}, 'psi',
  (ctx, Twb_R) => {
    let X = ctx.eval('C1/Twb_R + C2 + C3*Twb_R + C4*(Twb_R^2) +' +
      'C5*(Twb_R^3) + C6*(Twb_R^4) + C7*ln(Twb_R)', {
        Twb_R, C1, C2, C3, C4, C5, C6, C7, ln,
    }, 'X');
    return ctx.eval('exp(X)', {X}, 'Psat_I');
});

// Saturation pressure of water
export let calcPws = describeFunc('calcPws', {Twb_F: 'F'}, 'psi',
  (ctx, Twb_F) => {
    if (Twb_F > 32) {
      return ctx.call(calcPsat_W, toRankine(Twb_F));
    }  else {
      return ctx.call(calcPsat_I, toRankine(Twb_F));
    }
});

// Degree of saturation
// RH: [0-100]
export let calcDegSat = describeFunc('calcDegSat', {
    RH: 'Percent', Pws: 'psi', Ps: 'psi',
  }, 'None',
  (ctx, RH, Pws, Ps) => {
    let res = ctx.eval('RH/100.0 * ((1.0 - Pws/Ps) / (1.0 - (RH/100.0)*Pws/Ps))', {
      RH: RH, Pws: Pws, Ps: Ps,
    }, "u");
    return res;
});

// Humidity ratio at saturation
export let calcWs = describeFunc('calcWs', {
    Pws: 'psi', Ps: 'psi',
  }, 'None',
  (ctx, Pws, Ps) => {
    return ctx.eval('0.621945 * Pws/(Ps - Pws)', {
      Pws: Pws, Ps: Ps,
    }, 'Ws')
});

/**
 * Humidity ratio (W)
 */
export let calcHumidityRatio = describeFunc('calcHumidityRatio', {
  t_db_F: 'F',
  t_wb_F: 'F',
  W_s_star: 'None',
}, 'None', (ctx, t_db_F, t_wb_F, W_s_star) => {
  if (t_db_F <= 32) {
    ctx.W = ctx.eval('((1220 - 0.04*t_wb_F)*W_s_star - 0.24*(t_db_F - t_wb_F))/(1220 + 0.444*t_db_F - 0.48*t_wb_F)', {
      t_db_F: t_db_F,
      t_wb_F: t_wb_F,
      W_s_star: W_s_star,
    }, 'W');
  } else {
    ctx.W = ctx.eval('((1093 - 0.556*t_wb_F)*W_s_star - 0.24*(t_db_F - t_wb_F))/(1093 + 0.444*t_db_F - t_wb_F)', {
      t_db_F: t_db_F,
      t_wb_F: t_wb_F,
      W_s_star: W_s_star,
    }, 'W');
  }
  return ctx.eval('max(W, MIN_HUMIDITY_RATIO)', {
    MIN_HUMIDITY_RATIO: MIN_HUMIDITY_RATIO,
  }, 'W')
});

/**
 * Relative humidity (theta)
 * Output: Ratio, [0-1]
 */
export let calcRelativeHumidity = describeFunc('calcRelativeHumidity', {
  // degSat must be in [0-1]
  degSat: 'None',
  p_ws: 'psi',
  p_loc: 'psi',
}, 'Ratio', (ctx, degSat, p_ws, p_loc) => {
  /*
  if (!(0 <= degSat && degSat <= 1)) {
    throw new Error(`degSat must be in [0-1]. Got: ${degSat}`);
  }
  */
  return ctx.eval('degSat / (1-(1-degSat)*(p_ws/p_loc))', {degSat, p_ws, p_loc}, 'RH');
});

export let calcDegSatSimple = describeFunc('calcDegSatSimple', {
  W: 'None',
  Ws: 'None',
}, 'None', (ctx, W, Ws) => {
  return ctx.eval('W/Ws', {W, Ws}, 'u');
});

// t_wb_C: temp in C
// RH: [0-100]
export let calcWFull = describeFunc('calcWFull', {
    t_wb_C: 'C',
    RH: 'Percent',
    Ploc: 'psi',
  }, 'None', (ctx, t_wb_C, RH, Ploc) => {
  let Pws = ctx.call(calcPws, toFahr(t_wb_C));
  let degSat = ctx.call(calcDegSat, RH, Pws, Ploc);
  let Ws = ctx.call(calcWs, Pws, Ploc);
  return ctx.eval('Ws*degSat', {Ws, degSat}, 'W');
});

export let calcWFullKnownTwb = describeFunc('calcWFullKnownTwb', {
  t_db_C: 'C',
  t_wb_C: 'C',
  Ploc: 'psi',
}, 'None', (ctx, t_db_C, t_wb_C, Ploc) => {
  ctx.P_ws_star = ctx.call(calcPws, toFahr(t_wb_C));
  ctx.W_s_star = ctx.call(calcWs, ctx.P_ws_star, Ploc);
  return ctx.eval('((1093-0.556*T_wb)*W_s_star-0.24*(T_db-T_wb)) / (1093+0.444*T_db-T_wb)', {
    T_wb: toFahr(t_wb_C),
    T_db: toFahr(t_db_C),
    W_s_star: ctx.W_s_star,
  }, 'W');
});

// Enthalpy (h)
export let calcI = describeFunc('calcI', {
  Tdb: 'F', W: 'None'}, 'None',
  (ctx, Tdb, W) => {
    return ctx.eval('0.24*Tdb + W*(1061 + 0.444*Tdb)', {
      Tdb: Tdb, W: W,
    }, 'I');
});

export let calcIFull = describeFunc('calcIFull', {
  t_db_F: 'F',
  RH: 'Percent',
  P_loc: 'psi',
}, 'None', (ctx, t_db_F, RH, P_loc) => {
    let t_db_C = toCelsius(t_db_F);
    let t_wb_C = ctx.call(calcTwb, t_db_C, RH);
    let W = ctx.call(calcWFull, t_wb_C, RH, P_loc);
    let I = ctx.call(calcI, t_db_F, W);
    return [I, W];
})

export let calcIFullKnownTwb = describeFunc('calcIFullKnownTwb', {
  t_db_F: 'F',
  t_wb_F: 'F',
  P_loc: 'psi',
}, 'None', (ctx, t_db_F, t_wb_F, P_loc) => {
  let W = ctx.call(calcWFullKnownTwb, toCelsius(t_db_F), toCelsius(t_wb_F), P_loc);
  let I = ctx.call(calcI, t_db_F, W);
  return [I, W];
})

export let DewpointConsts = {
  C_14: 100.45, 
  C_15: 33.193,
  C_16: 2.319,
  C_17: 0.17074,
  C_18: 1.2063,
  C_19: 90.12,
  C_20: 26.142,
  C_21: 0.8927,
}

export let calcDewpointTemp = describeFunc('calcDewpointTemp', {
  t_db_F: 'F',
  W: 'None',
  P_loc: 'psi',
}, 'F', (ctx, t_db_F, W, P_loc) => {
  let p_w = ctx.eval('W*P_loc/(0.621945 + W)', {W, P_loc}, 'p_w');
  let alpha = ctx.eval('ln(p_w)', {p_w, ln}, 'alpha');
  if (t_db_F > 32) {
    return ctx.eval('C_14 + C_15*alpha + C_16*(alpha^2) + C_17*(alpha^3) + C_18*(p_w^(0.1984))', {
      alpha, p_w, ...DewpointConsts,
    }, 't_dp_F');
  } else {
    return ctx.eval('C_19 + C_20*alpha + C_21*(alpha^2)', {
      alpha, p_w, ...DewpointConsts,
    }, 't_dp_F');
  }
});

export let calcSpecificVolume = describeFunc('calcSpecificVolume', {
  t_db_F: 'F',
  W: 'None',
  P_loc: 'psi',
}, 'SpecificVolume', (ctx, t_db_F, W, P_loc) => {
  return ctx.eval('0.370486 * t_db_R * (1 + 1.607858*W) / P_loc', {
    t_db_R: toRankine(t_db_F),
    W: W,
    P_loc: P_loc,
  }, 'v');
})

export let calcTwbIteratively = describeFunc('calcTwbIteratively', {
  t_db_F: 'F',
  W: 'None',
  P_loc: 'psi',
}, 'F', (ctx, t_db_F, W, P_loc) => {
  // Bi-section method to find t_wb_F
  let t_wb_F_cur = t_db_F;
  let t_wb_F_prev = null;
  let C_prev = null;
  let maxIters = 20;
  let tolerance = 0.01;
  for (let i = 0; i < maxIters; ++i) {
    let p_ws_star = ctx.call(calcPws, t_wb_F_cur);
    let W_s_star = ctx.call(calcWs, p_ws_star, P_loc);
    let W_test = ctx.call(calcHumidityRatio, t_db_F, t_wb_F_cur, W_s_star);
    let C = ctx.eval('W_test - W', {W, W_test}, 'C');
    if (i == 0) {
      t_wb_F_prev = t_wb_F_cur;
      C_prev = C;
      t_wb_F_cur = t_wb_F_cur - 10;
    } else {
      if (C_prev*C < 0) {
        let t_wb_F_next = ctx.eval('t_wb_F_cur - (t_wb_F_cur - t_wb_F_prev)/2', {
          t_wb_F_cur: t_wb_F_cur,
          t_wb_F_prev: t_wb_F_prev,
        }, 't_wb_F_next')
        t_wb_F_prev = t_wb_F_cur;
        t_wb_F_cur = t_wb_F_next;
        C_prev = C;
      } else if (C_prev*C > 0) {
        let t_wb_F_next = ctx.eval('t_wb_F_cur - (t_wb_F_prev - t_wb_F_cur)', {
          t_wb_F_cur: t_wb_F_cur,
          t_wb_F_prev: t_wb_F_prev,
        }, 't_wb_F_next')
        t_wb_F_prev = t_wb_F_cur;
        t_wb_F_cur = t_wb_F_next;
        C_prev = C;
      } else if (C_prev*C == 0) {
        break;
      }
      if (Math.abs(t_wb_F_cur - t_wb_F_prev) < tolerance) {
        break;
      }
    }
  }

  return t_wb_F_cur;
});

export let CalcWithWetBulb = describeFunc('CalcWithWetBulb', {
  t_db_F: 'F',
  altitude_ft: 'ft',
  t_wb_F: 'F',
}, 'None', (ctx, t_db_F, altitude_ft, t_wb_F) => {
  let P_loc = ctx.call(calcP_loc, altitude_ft);
  let p_s = ctx.call(calcPws, t_db_F);
  let p_s_star = ctx.call(calcPws, t_wb_F);
  let W_s = ctx.call(calcWs, p_s, P_loc);
  let W_s_star = ctx.call(calcWs, p_s_star, P_loc);
  let W = ctx.call(calcHumidityRatio, t_db_F, t_wb_F, W_s_star);
  let degSat = ctx.call(calcDegSatSimple, W, W_s);
  let RH = ctx.call(calcRelativeHumidity, degSat, p_s, P_loc);
  let t_dp_F = ctx.call(calcDewpointTemp, t_db_F, W, P_loc);
  let v = ctx.call(calcSpecificVolume, t_db_F, W, P_loc);
  let h = ctx.call(calcI, t_db_F, W);

  return {
    t_db_F: t_db_F,
    t_wb_F: t_wb_F,    
    RH: RH,
    W: W,
    t_dp_F: t_dp_F,
    v: v,
    h: h,
  };
})

export let CalcWithRelativeHumidity = describeFunc('CalcWithRelativeHumidity', {
  t_db_F: 'F',
  altitude_ft: 'ft',
  // RH in [0-1]
  RH: 'Ratio',
}, 'None', (ctx, t_db_F, altitude_ft, RH) => {
  if (!(0 <= RH && RH <= 1)) {
    throw new Error("RH must be in [0-1]");
  }

  let P_loc = ctx.call(calcP_loc, altitude_ft);
  let p_s = ctx.call(calcPws, t_db_F);
  let W_s = ctx.call(calcWs, p_s, P_loc);

  // Humidity ratio:
  let degSat = ctx.eval('RH*(1 - p_s/P_loc) / (1 - RH*p_s/P_loc)', {
    RH: RH,
    p_s: p_s,
    P_loc: P_loc,
  }, 'degSat');
  let W = ctx.eval('degSat*W_s', {degSat, W_s}, 'W');
  W = ctx.eval('max(W, MIN_HUMIDITY_RATIO)', {
    W: W,
    MIN_HUMIDITY_RATIO: MIN_HUMIDITY_RATIO
  }, 'W');

  let t_wb_F = null;
  if (RH === 1.0) {
    // Bit of a hack here - the calcTwbIteratively function has trouble
    // converging when RH = 100%. So just set t_wb_F = t_dp_F in this case.
    t_wb_F = t_db_F;
  } else {
    t_wb_F = ctx.call(calcTwbIteratively, t_db_F, W, P_loc);
  }

  let t_dp_F = ctx.call(calcDewpointTemp, t_db_F, W, P_loc);
  let v = ctx.call(calcSpecificVolume, t_db_F, W, P_loc);
  let h = ctx.call(calcI, t_db_F, W);

  return {
    t_db_F: t_db_F,
    t_wb_F: t_wb_F,    
    RH: RH,
    W: W,
    t_dp_F: t_dp_F,
    v: v,
    h: h,
  };
})

export let CalcWithHumidityRatio = describeFunc('CalcWithHumidityRatio', {
  t_db_F: 'F',
  altitude_ft: 'ft',
  W: 'None',
}, 'None', (ctx, t_db_F, altitude_ft, W) => {
  if (W < 0) {
    throw new Error(`Humidity ratio must be >= 0. Found: ${W}`);
  }
  W = ctx.eval('max(W, MIN_HUMIDITY_RATIO)', {
    W: W,
    MIN_HUMIDITY_RATIO: MIN_HUMIDITY_RATIO
  }, 'W');

  let P_loc = ctx.call(calcP_loc, altitude_ft);
  let p_s = ctx.call(calcPws, t_db_F);
  let W_s = ctx.call(calcWs, p_s, P_loc);
  /*
  if (W > W_s) {
    throw new Error(`Got a W (${W}) value greater than W_s (${W_s}), which should not happen under normal conditions. Please check inputs.`);
  }
  */

  // Humidity ratio:
  let degSat = ctx.call(calcDegSatSimple, W, W_s);
  let RH = ctx.call(calcRelativeHumidity, degSat, p_s, P_loc);

  let t_wb_F = ctx.call(calcTwbIteratively, t_db_F, W, P_loc);

  let t_dp_F = ctx.call(calcDewpointTemp, t_db_F, W, P_loc);
  let v = ctx.call(calcSpecificVolume, t_db_F, W, P_loc);
  let h = ctx.call(calcI, t_db_F, W);

  return {
    t_db_F: t_db_F,
    t_wb_F: t_wb_F,    
    RH: RH,
    W: W,
    t_dp_F: t_dp_F,
    v: v,
    h: h,
  };
})

export let CalcWithDewpointTemp = describeFunc('CalcWithDewpointTemp', {
  t_db_F: 'F',
  altitude_ft: 'ft',
  t_dp_F: 'F',
}, 'None', (ctx, t_db_F, altitude_ft, t_dp_F) => {
  let P_loc = ctx.call(calcP_loc, altitude_ft);
  let p_s = ctx.call(calcPws, t_db_F);
  let W_s = ctx.call(calcWs, p_s, P_loc);

  // Humidity ratio:
  let p_ws_dp = ctx.call(calcPws, t_dp_F);
  let W = ctx.eval('0.621945 * p_ws_dp / (P_loc - p_ws_dp)', {p_ws_dp, P_loc}, 'W');

  let t_wb_F = ctx.call(calcTwbIteratively, t_db_F, W, P_loc);
  let degSat = ctx.call(calcDegSatSimple, W, W_s);
  let RH = ctx.call(calcRelativeHumidity, degSat, p_s, P_loc);

  let v = ctx.call(calcSpecificVolume, t_db_F, W, P_loc);
  let h = ctx.call(calcI, t_db_F, W);

  return {
    t_db_F: t_db_F,
    t_wb_F: t_wb_F,    
    RH: RH,
    W: W,
    t_dp_F: t_dp_F,
    v: v,
    h: h,
  };
})

export let CalcWithSpecificVolume = describeFunc('CalcWithSpecificVolume', {
  t_db_F: 'F',
  altitude_ft: 'ft',
  v: 'SpecificVolume',
}, 'None', (ctx, t_db_F, altitude_ft, v) => {
  let P_loc = ctx.call(calcP_loc, altitude_ft);
  let p_s = ctx.call(calcPws, t_db_F);
  let W_s = ctx.call(calcWs, p_s, P_loc);

  // Humidity ratio:
  let W = ctx.eval('(1/0.370486)*(v*P_loc)/t_db_R', {v, P_loc, t_db_R: toRankine(t_db_F)}, 'W');

  let t_wb_F = ctx.call(calcTwbIteratively, t_db_F, W, P_loc);
  let degSat = ctx.call(calcDegSatSimple, W, W_s);
  let RH = ctx.call(calcRelativeHumidity, degSat, p_s, P_loc);

  let t_dp_F = ctx.call(calcDewpointTemp, t_db_F, W, P_loc);
  let h = ctx.call(calcI, t_db_F, W);

  return {
    t_db_F: t_db_F,
    t_wb_F: t_wb_F,    
    RH: RH,
    W: W,
    t_dp_F: t_dp_F,
    v: v,
    h: h,
  };
})

export let CalcWithEnthalpy = describeFunc('CalcWithEnthalpy', {
  t_db_F: 'F',
  altitude_ft: 'ft',
  h: 'None',
}, 'None', (ctx, t_db_F, altitude_ft, h) => {
  let P_loc = ctx.call(calcP_loc, altitude_ft);
  let p_s = ctx.call(calcPws, t_db_F);
  let W_s = ctx.call(calcWs, p_s, P_loc);

  // Humidity ratio:
  let W = ctx.eval('(h - 0.24*t_db_F) / (1061 + 0.444*t_db_F)', {h, t_db_F}, 'W');
  W = ctx.eval('max(W, MIN_HUMIDITY_RATIO)', {
    W: W,
    MIN_HUMIDITY_RATIO: MIN_HUMIDITY_RATIO
  }, 'W');

  let t_wb_F = ctx.call(calcTwbIteratively, t_db_F, W, P_loc);
  let degSat = ctx.call(calcDegSatSimple, W, W_s);
  let RH = ctx.call(calcRelativeHumidity, degSat, p_s, P_loc);

  let t_dp_F = ctx.call(calcDewpointTemp, t_db_F, W, P_loc);
  let v = ctx.call(calcSpecificVolume, t_db_F, W, P_loc);

  return {
    t_db_F: t_db_F,
    t_wb_F: t_wb_F,    
    RH: RH,
    W: W,
    t_dp_F: t_dp_F,
    v: v,
    h: h,
  };
})

export let PsyCalcMethod = {
  CalcWithWetBulbTemp: 'CalcWithWetBulbTemp',
  CalcWithRelativeHumidity: 'CalcWithRelativeHumidity',
  CalcWithHumidityRatio: 'CalcWithHumidityRatio',
  CalcWithDewpointTemp: 'CalcWithDewpointTemp',
  CalcWithSpecificVolume: 'CalcWithSpecificVolume',
  CalcWithEnthalpy: 'CalcWithEnthalpy',
};

// Calculate a curve of constant relative humidity, in terms of 
// dry bulb temperature vs. humidity ratio
export let CalcRelativeHumidityCurve = describeFunc('CalcRelativeHumidityCurve', {
  altitude_ft: 'ft',
  relativeHumidity: 'Ratio',
  minDryBulbTemp: 'F',
  maxDryBulbTemp: 'F',
  stepSize: 'F',
  maxHumidityRatio: 'None',
}, 'None', (ctx, altitude_ft, relativeHumidity, minDryBulbTemp, maxDryBulbTemp, stepSize, maxHumidityRatio) => {
  let P_loc = ctx.call(calcP_loc, altitude_ft);
  let points = [];
  
  for (let t_db_F = minDryBulbTemp; t_db_F <= maxDryBulbTemp; t_db_F += stepSize) {
    let p_s = ctx.call(calcPws, t_db_F);
    let W_s = ctx.call(calcWs, p_s, P_loc);
    
    // Calculate humidity ratio for the given relative humidity
    let degSat = ctx.eval('relativeHumidity*(1 - p_s/P_loc) / (1 - relativeHumidity*p_s/P_loc)', {
      relativeHumidity: relativeHumidity,
      p_s: p_s,
      P_loc: P_loc,
    }, 'degSat');
    let W = ctx.eval('degSat*W_s', {degSat, W_s}, 'W');

    points.push({
      T_db: t_db_F,
      HumidityRatio: W
    });

    // Break once humidity ratio exceeds the maximum.
    // All further points will be fully off the chart.
    if (W > maxHumidityRatio) {
      break;
    }
  }
  
  return points;
});

// Calculate a curve of constant enthalpy, in terms of
// dry bulb temperature vs. humidity ratio
export let CalcEnthalpyCurve = describeFunc('CalcEnthalpyCurve', {
  altitude_ft: 'ft',
  enthalpy: 'None',
  minDryBulbTemp: 'F',
  maxDryBulbTemp: 'F',
  stepSize: 'F',
  maxHumidityRatio: 'None',
}, 'None', (ctx, altitude_ft, enthalpy, minDryBulbTemp, maxDryBulbTemp, stepSize, maxHumidityRatio) => {
  let points = [];
  
  for (let t_db_F = minDryBulbTemp; t_db_F <= maxDryBulbTemp; t_db_F += stepSize) {
    // Calculate humidity ratio for the given enthalpy and dry bulb temperature
    // From enthalpy equation: h = 0.24*t_db_F + W*(1061 + 0.444*t_db_F)
    // Solving for W: W = (h - 0.24*t_db_F) / (1061 + 0.444*t_db_F)
    let W = ctx.eval('(enthalpy - 0.24*t_db_F) / (1061 + 0.444*t_db_F)', {
      enthalpy: enthalpy,
      t_db_F: t_db_F
    }, 'W');
    
    points.push({
      T_db: t_db_F,
      HumidityRatio: W
    });

    // If we're off the chart, stop adding points
    if (W < 0) {
      break;
    }
  }
  
  return points;
});

// Private helper function to find intersection between two line segments
// Takes 4 points: (x1,y1)-(x2,y2) and (x3,y3)-(x4,y4)
// Returns intersection point {x, y} or null if no intersection
function FindLineIntersect(x1, y1, x2, y2, x3, y3, x4, y4) {
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  
  // Lines are parallel
  if (Math.abs(denom) < 1e-10) {
    return null;
  }
  
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
  
  // Check if intersection is within both line segments
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return {
      x: x1 + t * (x2 - x1),
      y: y1 + t * (y2 - y1)
    };
  }
  
  return null;
}

export let TrimToSaturationCurve = describeFunc('TrimToSaturationCurve', {
  saturationCurve: 'None',
  curve2: 'None',
  maxHumidityRatio: 'None',
}, 'None', (ctx, saturationCurve, curve2, maxHumidityRatio) => {
  // Finds the start index in curve2 where it intersects with saturationCurve, and
  // trims curve2 to start from that index.
  // Curve 2 must have the same X-axis points as saturationCurve, and it must
  // start above the curve and go below it at some point. 
  // Returns the trimmed curve2.
  
  let startIndex = 0;
  for (let i = 0; i < curve2.length; ++i) {
    let curve2Point = curve2[i];
    if (curve2Point.HumidityRatio > maxHumidityRatio) {
      startIndex = i;
      continue;
    }
    if (i < saturationCurve.length) {
      let satPoint = saturationCurve[i];
      if (satPoint.T_db != curve2Point.T_db) {
        throw new Error(`X-axis points do not match between saturationCurve and curve2 at index ${i}.`);
      }
      let W_sat = satPoint.HumidityRatio;
      if (curve2Point.HumidityRatio <= W_sat) {
        startIndex = i;

        // Insert an interpolated point at the intersection
        if (i > 0) {
          let prevCurve2Point = curve2[i - 1];
          let prevSatPoint = saturationCurve[i - 1];
          
          let intersection = FindLineIntersect(
            prevCurve2Point.T_db, prevCurve2Point.HumidityRatio,
            curve2Point.T_db, curve2Point.HumidityRatio,
            prevSatPoint.T_db, prevSatPoint.HumidityRatio,
            satPoint.T_db, W_sat
          );
          
          if (intersection) {
            let intersectionPoint = {
              T_db: intersection.x,
              HumidityRatio: intersection.y
            };
            curve2.splice(i, 0, intersectionPoint);
          }
        }

        break;
      }
    }
  }

  return curve2.slice(startIndex);
});

export let CalcPsychrometrics = describeFunc('CalcPsychrometrics', {
  t_db_F: 'F',
  altitude_ft: 'ft',
  method: 'None',
  inputs: 'None',
}, 'None', (ctx, t_db_F, altitude_ft, method, inputs) => {
  // Clamp input temperatures to absolute zero, so nonsensical inputs can't blow up
  // the calculations below.
  t_db_F = Math.max(t_db_F, ABSOLUTE_ZERO_F);
  if (inputs.t_wb_F !== undefined) {
    inputs = {...inputs, t_wb_F: Math.max(inputs.t_wb_F, ABSOLUTE_ZERO_F)};
  }
  if (inputs.t_dp_F !== undefined) {
    inputs = {...inputs, t_dp_F: Math.max(inputs.t_dp_F, ABSOLUTE_ZERO_F)};
  }

  let funcArgs = {
    t_db_F: t_db_F,
    altitude_ft: altitude_ft,
    method: method,
    inputs: inputs,
  }
  let cachedVal = ctx.funcCache.get('CalcPsychrometrics', funcArgs);
  if (cachedVal) {
    ctx.log('Using cached value');
    return cachedVal;
  }

  let res = null;
  if (method == PsyCalcMethod.CalcWithWetBulbTemp) {
    res = ctx.call(CalcWithWetBulb, t_db_F, altitude_ft, inputs.t_wb_F);
  } else if (method == PsyCalcMethod.CalcWithRelativeHumidity) {
    res = ctx.call(CalcWithRelativeHumidity, t_db_F, altitude_ft, inputs.RH);
  } else if (method == PsyCalcMethod.CalcWithHumidityRatio) {
    res = ctx.call(CalcWithHumidityRatio, t_db_F, altitude_ft, inputs.W);
  } else if (method == PsyCalcMethod.CalcWithDewpointTemp) {
    res = ctx.call(CalcWithDewpointTemp, t_db_F, altitude_ft, inputs.t_dp_F);
  } else if (method == PsyCalcMethod.CalcWithSpecificVolume) {
    res = ctx.call(CalcWithSpecificVolume, t_db_F, altitude_ft, inputs.v);
  } else if (method == PsyCalcMethod.CalcWithEnthalpy) {
    res = ctx.call(CalcWithEnthalpy, t_db_F, altitude_ft, inputs.h);
  } else {
    throw new Error(`Unknown method: ${method}`);
  }
  ctx.funcCache.set('CalcPsychrometrics', funcArgs, res);
  return res;
})
